import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { User } from "@supabase/supabase-js";
import { getSupabase } from "@/lib/supabase-lazy";

export type AuthResult = { ok: boolean; cancelled?: boolean; message?: string };

type AuthState = {
  user: User | null;
  loading: boolean;
  isAnonymous: boolean;
  signInWithGoogle: () => Promise<AuthResult>;
  signInWithEmail: (email: string, password: string) => Promise<AuthResult>;
  signUpWithEmail: (email: string, password: string) => Promise<AuthResult>;
  sendPasswordReset: (email: string) => Promise<AuthResult>;
  updatePassword: (password: string) => Promise<AuthResult>;
  continueAsGuest: () => Promise<AuthResult>;
  linkGoogle: () => Promise<AuthResult>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

function friendlyMessage(error: unknown): string {
  const raw = error instanceof Error ? error.message : String(error ?? "");
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return "You're offline — check your connection and try again.";
  }
  const lower = raw.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("network")) {
    return "You're offline — check your connection and try again.";
  }
  if (lower.includes("already") && lower.includes("link")) {
    return "That Google account is already connected to another profile.";
  }
  if (lower.includes("weak") && lower.includes("password")) {
    return "That password is too easy to guess. Try 8+ characters mixing letters, numbers and a symbol — something you don't use elsewhere.";
  }
  if (lower.includes("identity_already_exists")) {
    return "That Google account is already connected to another profile.";
  }
  return raw || "We couldn't sign you in. Please try again.";
}

function isCancellation(error: unknown): boolean {
  const raw = (error instanceof Error ? error.message : String(error ?? "")).toLowerCase();
  return (
    raw.includes("closed") ||
    raw.includes("cancel") ||
    raw.includes("aborted") ||
    raw.includes("popup")
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | null = null;

    void (async () => {
      const supabase = await getSupabase();
      if (!active) return;

      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!active) return;
        setUser(session?.user ?? null);
      });
      unsubscribe = () => sub.subscription.unsubscribe();

      supabase.auth
        .getSession()
        .then(({ data }) => {
          if (!active) return;
          setUser(data.session?.user ?? null);
        })
        .catch(() => {
          if (active) setUser(null);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    })();

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  const signInWithGoogle = useCallback(async (): Promise<AuthResult> => {
    try {
      const { lovable } = await import("@/integrations/lovable");
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: window.location.origin,
      });

      if (result.redirected) return { ok: true };
      if (result.error) {
        return isCancellation(result.error)
          ? { ok: false, cancelled: true }
          : { ok: false, message: friendlyMessage(result.error) };
      }
      return { ok: true };
    } catch (error) {
      return isCancellation(error)
        ? { ok: false, cancelled: true }
        : { ok: false, message: friendlyMessage(error) };
    }
  }, []);

  const continueAsGuest = useCallback(async (): Promise<AuthResult> => {
    try {
      const { error } = await (await getSupabase()).auth.signInAnonymously();
      if (error) return { ok: false, message: friendlyMessage(error) };
      return { ok: true };
    } catch (error) {
      return { ok: false, message: friendlyMessage(error) };
    }
  }, []);

  const signInWithEmail = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        const { error } = await (await getSupabase()).auth.signInWithPassword({ email, password });
        if (error) {
          const lower = error.message.toLowerCase();
          return {
            ok: false,
            message: lower.includes("invalid login")
              ? "Wrong email or password. Please try again."
              : friendlyMessage(error),
          };
        }
        return { ok: true };
      } catch (error) {
        return { ok: false, message: friendlyMessage(error) };
      }
    },
    [],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        const { data, error } = await (await getSupabase()).auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (error) {
          const lower = error.message.toLowerCase();
          return {
            ok: false,
            message: lower.includes("already registered")
              ? "That email already has an account — sign in instead."
              : friendlyMessage(error),
          };
        }
        if (!data.session) {
          return { ok: true, message: "Check your inbox to confirm your email, then sign in." };
        }
        return { ok: true };
      } catch (error) {
        return { ok: false, message: friendlyMessage(error) };
      }
    },
    [],
  );

  const sendPasswordReset = useCallback(async (email: string): Promise<AuthResult> => {
    try {
      const { error } = await (await getSupabase()).auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) return { ok: false, message: friendlyMessage(error) };
      return { ok: true, message: "Password reset link sent — check your email." };
    } catch (error) {
      return { ok: false, message: friendlyMessage(error) };
    }
  }, []);

  const updatePassword = useCallback(async (password: string): Promise<AuthResult> => {
    try {
      const { error } = await (await getSupabase()).auth.updateUser({ password });
      if (error) return { ok: false, message: friendlyMessage(error) };
      return { ok: true };
    } catch (error) {
      return { ok: false, message: friendlyMessage(error) };
    }
  }, []);

  const linkGoogle = useCallback(async (): Promise<AuthResult> => {
    try {
      const { error } = await (await getSupabase()).auth.linkIdentity({
        provider: "google",
        options: { redirectTo: window.location.origin },
      });
      if (error) {
        return isCancellation(error)
          ? { ok: false, cancelled: true }
          : { ok: false, message: friendlyMessage(error) };
      }
      return { ok: true };
    } catch (error) {
      return { ok: false, message: friendlyMessage(error) };
    }
  }, []);

  const signOut = useCallback(async () => {
    try {
      await (await getSupabase()).auth.signOut();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      isAnonymous: user?.is_anonymous === true,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      updatePassword,
      continueAsGuest,
      linkGoogle,
      signOut,
    }),
    [
      user,
      loading,
      signInWithGoogle,
      signInWithEmail,
      signUpWithEmail,
      sendPasswordReset,
      updatePassword,
      continueAsGuest,
      linkGoogle,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}